// F1 App-Shell (TES-948): geteiltes Avatar-/User-Dropdown (Profil + Logout).
// Von SideNav (Footer, data-tour="user-menu") und MobileTopBar genutzt.
// Avatar + Trigger in Sub-Komponenten zerlegt, damit keine Funktion die
// fallow-CRAP-Schwelle reisst.
// WICHTIG (TES-1022): Die Trigger-Sub-Komponenten MUESSEN forwardRef sein und
// die restlichen Props an <Button> durchreichen — DropdownMenuTrigger asChild
// injiziert seine Handler (onPointerDown/onKeyDown/aria/ref) als Props in das
// Kind; ohne Forwarding oeffnet das Menue nicht (Logout/Profil unerreichbar).
import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { Link } from "react-router";
import { User, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { AuthenticatedImage } from "@/components/ui/AuthenticatedImage";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { ShellModel } from "./useShellModel";
import { roleBadgeVariants, sidebarRoleBadgeVariants } from "./navConfig";

interface UserMenuProps {
  model: ShellModel;
  variant: "sidebar" | "compact";
  collapsed?: boolean;
  dataTour?: string;
}

// Rollen-Badge mit i18n-Label (geteilt zwischen Trigger und Dropdown-Header).
function RoleBadge({
  model,
  className,
  surface = "default",
}: {
  model: ShellModel;
  className?: string;
  surface?: "default" | "sidebar";
}) {
  const { t, primaryRole } = model;
  const variants = surface === "sidebar" ? sidebarRoleBadgeVariants : roleBadgeVariants;
  return (
    <Badge variant="outline" className={cn(className, variants[primaryRole])}>
      {t(`roles.${primaryRole}`)}
    </Badge>
  );
}

// Avatar (Bild via Proxy-URL, sonst Initialen-Fallback).
function UserAvatar({ model, compact }: { model: ShellModel; compact: boolean }) {
  const { displayName, initials, avatarProxyUrl, profileUpdatedAt } = model;
  return (
    <Avatar className="flex-shrink-0 rounded-full overflow-hidden h-8 w-8">
      {avatarProxyUrl ? (
        <AuthenticatedImage
          src={avatarProxyUrl}
          alt={displayName}
          className="h-8 w-8 min-w-8 min-h-8 object-cover"
          showSkeleton={false}
          showErrorUI={false}
          cacheBuster={profileUpdatedAt}
        />
      ) : null}
      <AvatarFallback
        className={cn(
          "text-sm",
          compact
            ? "bg-primary-container text-primary-foreground text-xs"
            : "bg-sidebar-primary text-sidebar-primary-foreground",
        )}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

// Kompakter Trigger (MobileTopBar): nur Avatar-Button. forwardRef + Prop-Spread
// sind Pflicht fuer DropdownMenuTrigger asChild (s. Kopf-Kommentar).
interface CompactTriggerProps extends ComponentPropsWithoutRef<typeof Button> {
  model: ShellModel;
}

const CompactTrigger = forwardRef<HTMLButtonElement, CompactTriggerProps>(
  ({ model, className, ...props }, ref) => (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      className={cn("h-9 w-9 rounded-full", className)}
      aria-label={model.displayName}
      data-testid="user-menu-trigger"
      {...props}
    >
      <UserAvatar model={model} compact />
    </Button>
  ),
);
CompactTrigger.displayName = "CompactTrigger";

// Sidebar-Trigger: Avatar + Name + Rollen-Badge (Name/Badge nur expanded).
interface SidebarTriggerProps extends ComponentPropsWithoutRef<typeof Button> {
  model: ShellModel;
  collapsed: boolean;
}

const SidebarTrigger = forwardRef<HTMLButtonElement, SidebarTriggerProps>(
  ({ model, collapsed, className, ...props }, ref) => (
    <Button
      ref={ref}
      variant="ghost"
      data-testid="user-menu-trigger"
      className={cn(
        "w-full flex items-center gap-3 py-2.5 h-auto rounded-lg hover:bg-sidebar-accent transition-colors",
        collapsed ? "px-2 justify-center" : "px-3",
        className,
      )}
      {...props}
    >
      <UserAvatar model={model} compact={false} />
      {!collapsed && (
        <div className="flex-1 min-w-0 text-left">
          <p className="text-sm font-medium truncate leading-tight">{model.displayName}</p>
          <RoleBadge
            model={model}
            surface="sidebar"
            className="mt-0.5 text-[10px] h-4 px-1.5 font-normal border"
          />
        </div>
      )}
    </Button>
  ),
);
SidebarTrigger.displayName = "SidebarTrigger";

export function UserMenu({ model, variant, collapsed = false, dataTour }: UserMenuProps) {
  const { t, displayName, signOut } = model;
  const compact = variant === "compact";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild data-tour={dataTour}>
        {compact ? (
          <CompactTrigger model={model} />
        ) : (
          <SidebarTrigger model={model} collapsed={collapsed} />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col gap-1">
            <span>{displayName}</span>
            <RoleBadge model={model} className="w-fit text-xs" />
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profile" className="flex items-center">
            <User className="mr-2 h-4 w-4" />
            {t("nav.profile")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive"
          onClick={signOut}
          data-testid="logout-button"
        >
          <LogOut className="mr-2 h-4 w-4" />
          {t("nav.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
